import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  RefreshCw, 
  Calendar, 
  AlertTriangle, 
  CheckCircle,
  Loader2,
  Bell
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionManager = ({ member, membershipPlan, onUpdate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [member]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/status`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAutoRenew = async () => {
    setToggleLoading(true);
    try {
      const response = await axios.post(`${API}/subscriptions/toggle-auto-renew`);
      toast.success(response.data.message);
      setSubscriptionStatus(prev => ({
        ...prev,
        auto_renew_enabled: response.data.auto_renew_enabled
      }));
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error toggling auto-renew:', error);
      toast.error(error.response?.data?.detail || 'Failed to update setting');
    } finally {
      setToggleLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="text-sm text-gray-500 mt-2">{t('subscriptionManager.loadingDetails')}</p>
        </CardContent>
      </Card>
    );
  }

  // No membership status available
  if (!subscriptionStatus || subscriptionStatus.status === 'none') {
    return null;
  }

  const isActive = subscriptionStatus.status === 'active';
  const autoRenewEnabled = subscriptionStatus.auto_renew_enabled;
  const isExpiringSoon = member?.membership_end_date && 
    new Date(member.membership_end_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  return (
    <Card className={`border-2 ${isExpiringSoon && !autoRenewEnabled ? 'border-orange-300' : 'border-teal-200'}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-teal-600" />
            <div>
              <CardTitle>{t('subscriptionManager.membershipSettings')}</CardTitle>
              <CardDescription>{t('subscriptionManager.manageRenewal')}</CardDescription>
            </div>
          </div>
          <Badge className={isActive ? 'bg-green-500' : 'bg-gray-500'}>
            {isActive ? t('membership.active') : subscriptionStatus.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Membership Info */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{t('subscriptionManager.currentPlan')}</span>
            <span className="font-semibold text-gray-900">{subscriptionStatus.plan_name}</span>
          </div>
          
          {subscriptionStatus.membership_end_date && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                <Calendar className="h-4 w-4 inline mr-1" />
                {autoRenewEnabled ? t('subscriptionManager.renewalDate') : t('subscriptionManager.expiresOn')}
              </span>
              <span className="font-semibold text-gray-900">
                {new Date(subscriptionStatus.membership_end_date).toLocaleDateString()}
              </span>
            </div>
          )}
          
          {autoRenewEnabled && subscriptionStatus.next_billing_amount && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{t('subscriptionManager.nextPayment')}</span>
              <span className="font-semibold text-gray-900">
                ${subscriptionStatus.next_billing_amount?.toFixed(2)}
              </span>
            </div>
          )}
        </div>

        {/* Auto-Renewal Toggle */}
        <div className="flex items-center justify-between p-4 bg-teal-50 rounded-lg">
          <div className="flex items-center gap-3">
            <Bell className={`h-5 w-5 ${autoRenewEnabled ? 'text-teal-600' : 'text-gray-400'}`} />
            <div>
              <Label htmlFor="auto-renew" className="font-semibold">
                {t('subscriptionManager.autoRenewal')}
              </Label>
              <p className="text-sm text-gray-600">
                {autoRenewEnabled 
                  ? t('subscriptionManager.receiveReminder')
                  : t('subscriptionManager.enableReminders')
                }
              </p>
            </div>
          </div>
          <Switch
            id="auto-renew"
            checked={autoRenewEnabled}
            onCheckedChange={handleToggleAutoRenew}
            disabled={toggleLoading}
          />
        </div>

        {/* Expiring Soon Warning */}
        {isExpiringSoon && !autoRenewEnabled && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-orange-800">{t('subscriptionManager.expiringSoonWarning')}</p>
                <p className="text-sm text-orange-700 mt-1">
                  {t('subscriptionManager.expiresOnDate', { date: new Date(member.membership_end_date).toLocaleDateString() })} {' '}
                  {t('subscriptionManager.enableAutoRenewal')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info about renewal */}
        <div className="text-sm text-gray-500 flex items-start gap-2">
          <CheckCircle className="h-4 w-4 text-teal-600 flex-shrink-0 mt-0.5" />
          <p>
            {autoRenewEnabled 
              ? t('subscriptionManager.emailReminder')
              : t('subscriptionManager.manualRenew')
            }
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default SubscriptionManager;
